import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Form, Container, Row, Col, Button } from 'react-bootstrap';
import { FaEdit, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import PaginationComponent from '../Pagination/PaginationComponent';
import { getAllUsers } from '../../services/Users/userService';
import CreateUserModal from './CreateUserModal';
import EditUserModal from './EditUserModal';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Users = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const canView = useMemo(() => hasPermission('usersView'), [hasPermission, permissions]);
  const canCreate = useMemo(() => hasPermission('usersCreate'), [hasPermission, permissions]);
  const canUpdate = useMemo(() => hasPermission('usersUpdate'), [hasPermission, permissions]);

  const fetchUsers = useCallback(async () => {
    if (!canView) return;
    try {
      const data = await getAllUsers(page - 1, pageSize);
      setUsers(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setNumberOfElements(data.numberOfElements);
    } catch (error) {}
  }, [page, pageSize, canView]);

  useEffect(() => {
    if (canView) {
      fetchUsers();
    }
  }, [fetchUsers, canView]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value, 10));
    setPage(1);
  };

  const handleOpenCreateUserModal = () => {
    if (!checkPermission('usersCreate')) {
      return;
    }
    setShowCreateUserModal(true);
  };
  const handleCloseCreateUserModal = () => setShowCreateUserModal(false);

  const handleOpenEditUserModal = (user) => {
    if (!checkPermission('usersUpdate')) {
      return;
    }
    setSelectedUser(user);
    setShowEditUserModal(true);
  };
  const handleCloseEditUserModal = () => {
    setSelectedUser(null);
    setShowEditUserModal(false);
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Controle de Usuários</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Controle de Usuários</h3>
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs={12} md="auto" className="d-flex align-items-center justify-content-md-end justify-content-center">
          <Form.Label className="me-2 mb-0">Mostrar</Form.Label>
          <Form.Select
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: 'auto' }}
            className="me-2"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </Col>
        <Col xs={12} md="auto" className="d-flex justify-content-md-end justify-content-center mt-2 mt-md-0">
          {canCreate && (
            <Button variant="primary" className="button-custom" onClick={handleOpenCreateUserModal}>
              Cadastrar Usuário
            </Button>
          )}
        </Col>
      </Row>
      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive="sm">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Usuário</th>
              <th>Permissão</th>
              <th style={{textAlign: 'center', width: '50px' }}>Ativo</th>
              <th style={{textAlign: 'center', width: '50px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.fullname}</td>
                <td>{user.username}</td>
                <td>{user.role}</td>
                <td style={{textAlign: 'center'}}>
                  {user.active ? (
                    <FaCheckCircle style={{ color: 'green' }} />
                  ) : (
                    <FaTimesCircle style={{ color: 'red' }} />
                  )}
                </td>
                <td style={{textAlign: 'center'}}>
                  {canUpdate && (
                    <Button
                      variant="link"
                      onClick={() => handleOpenEditUserModal(user)}
                      className="p-0"
                      style={{ fontSize: '1.2rem' }}
                    >
                      <FaEdit className='icon-custom'/>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <PaginationComponent
        totalPages={totalPages}
        currentPage={page}
        onPageChange={handlePageChange}
        totalElements={totalElements}
        size={pageSize}
        pageNumber={page - 1}
        numberOfElements={numberOfElements}
      />

      <CreateUserModal
        show={showCreateUserModal}
        handleClose={handleCloseCreateUserModal}
        refreshUsers={fetchUsers}
      />

      <EditUserModal
        show={showEditUserModal}
        handleClose={handleCloseEditUserModal}
        user={selectedUser}
        refreshUsers={fetchUsers}
      />
    </Container>
  );
};

export default Users;